import styled from '@emotion/styled'

import { MobileCardFrame } from '../../../../components/advice/shared'
import { VALUE_COLUMN_WIDTH, ROW_TO_SENTENCE_GAP, nameGutterStyles } from '../../elements'

export { HeaderRow, ValueRow, RowGroups, ValueColumnHeading, BalanceGutter } from '../../elements'

const FONT_FAMILY = "'Open Sans', 'Helvetica Neue', sans-serif"

export const Heading = styled.h1`
  width: 90%;
  font-weight: normal;
  font-family: ${FONT_FAMILY};
  font-size: 30px;
  line-height: 1.3;
  text-align: center;
  max-width: 760px;
  margin: 0;
  color: ${p => p.theme.mediumGray};

  strong {
    font-weight: bold;
    color: ${p => p.theme.darkestGray};
  }

  @media (max-width: 600px) {
    font-size: 24px;
  }
`

// Use a div so the tooltip's paragraph content is not nested inside a <p>.
export const Subheading = styled.div`
  font-family: 'Helvetica Neue', 'Open Sans', sans-serif;
  font-size: 18px;
  line-height: 1.4;
  text-align: center;
  margin: 10px 0 0;
  color: ${p => p.theme.lightestGray};

  @media (max-width: 600px) {
    width: 80%;
    margin-bottom: 35px;
  }
`

export const HelpAnchor = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-left: 6px;
`

// ---------------------------------------------------------------------------
// Data table
// ---------------------------------------------------------------------------

export const TableWrap = styled.div`
  width: 100%;
  max-width: 760px;
  margin-top: 48px;

  @media (max-width: 600px) {
    margin-top: 28px;
  }
`

// Desktop: only the two value columns (CURRENT / Your changes-Recommended) are
// centered on the page. Every row is a full-width flex line with an equal-flex
// gutter on each side of the value columns; the label is rendered into the left
// gutter (see LabelCell) and hangs to the left, while the empty right gutter
// (BalanceGutter) balances it so the value block stays centered. Because the label
// is in normal flow, a wrapped multi-line name grows the row height instead of
// overflowing, so it can never overlap the change text or the next holding.
export const DesktopTable = styled.div`
  width: 100%;
  margin-top: 35px;
  display: flex;
  flex-direction: column;
`

// A single holding: the value row plus an optional change sentence beneath it.
export const RowGroup = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${ROW_TO_SENTENCE_GAP};
`

// The left gutter, holding the section label + holding name. In normal flow
// (flex: 1) so long names (row.name comes straight from securityName) wrap and
// grow the row height rather than overflowing; min-width: 0 lets it shrink and
// wrap so the two gutters stay equal and the value block stays centered.
export const LabelCell = styled.div`
  ${nameGutterStyles}
`

export const SectionLabel = styled.div`
  font-family: ${FONT_FAMILY};
  font-size: 13px;
  color: ${p => p.theme.mediumGray};
`

export const StockName = styled.div`
  font-family: ${FONT_FAMILY};
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.darkestGray};

  @media (max-width: 600px) {
    margin-bottom: 5px;
  }
`

export const ValueCell = styled.div`
  width: ${VALUE_COLUMN_WIDTH};
  box-sizing: border-box;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${FONT_FAMILY};
  font-size: 15px;
  font-weight: ${p => (p.planned ? 700 : 400)};
  color: ${p => p.theme.darkestGray};
  background-color: ${p => (p.planned ? p.theme.ternaryColor : p.theme.addButtonHoverColor)};
  /* Planned amounts are bold; keep the currency sign and "Unable to sell" regular. */
  & > span {
    font-weight: 400;
  }
`

// Color only the currency sign; UnableToSell colors the full message separately.
export const Sign = styled.span`
  color: ${p => (p.planned ? p.theme.primaryColor : p.theme.lightestGray)};
`

export const UnableToSell = styled.span`
  color: ${p => p.theme.primaryColor};
`

// The change sentence spans both value columns, centered under the value row.
export const ChangeText = styled.div`
  width: 100%;
  font-family: ${FONT_FAMILY};
  font-size: 13px;
  text-align: center;
  color: ${p => p.theme.darkestGray};
`

// Mobile: one full-width bordered card per holding.

export const MobileCards = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  width: 100%;
`

export const Card = styled(MobileCardFrame)`
  padding: 16px;
  text-align: center;
`

export const CardChangeText = styled.div`
  font-family: ${FONT_FAMILY};
  font-size: 13px;
  color: ${p => p.theme.darkestGray};
  margin: 10px 0 30px;
`

export const CardValues = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 12px;
  margin-bottom: 20px;
`

export const CardValue = styled.div`
  flex: 1 1 0;
  font-family: ${FONT_FAMILY};
  font-size: 15px;
  font-weight: ${p => (p.planned ? 600 : 400)};
  color: ${p => (p.planned ? p.theme.primaryColor : p.theme.mediumGray)};
`

export const Arrow = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  color: ${p => p.theme.primaryColor};
`

// ---------------------------------------------------------------------------
// Account-balance lock-up accordion
// ---------------------------------------------------------------------------

export const AccordionWrap = styled.div`
  width: 50%;
  max-width: 760px;
  min-width: 350px;
  margin-top: 40px;
`

export const AccordionTrigger = styled.button`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  column-gap: 12px;
  /* Shrink to the icon + question, then center the whole group on the screen
     while the question text stays left-aligned within it. */
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
  padding: 0;
  text-align: left;

  & svg {
    fill: ${p => p.theme.lightestGray};
  }
`

export const AccordionQuestion = styled.span`
  font-family: 'Helvetica Neue', 'Open Sans', sans-serif;
  font-size: 18px;
  line-height: 1.35;
  color: ${p => p.theme.lightestGray};
  max-width: 340px;
`

export const IconBox = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  width: 24px;
  height: 24px;

  & svg {
    width: 100%;
    height: 100%;
  }
`

export const AccordionBody = styled.div`
  margin-top: 30px;

  @media (max-width: 600px) {
    margin-top: 20px;
  }
`

export const BalanceBlock = styled.div`
  text-align: center;
`

export const BalanceName = styled.div`
  font-family: ${FONT_FAMILY};
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.darkestGray};
`

export const BalanceAmount = styled.div`
  font-family: ${FONT_FAMILY};
  font-size: 18px;
  color: ${p => p.theme.mediumGray};
  margin-top: 10px;
`

export const Caption = styled.div`
  font-family: ${FONT_FAMILY};
  font-size: 14px;
  font-weight: 300;
  line-height: 1.3;
  color: ${p => p.theme.lightestGray};
  margin-top: ${p => (p.pie ? '-5px' : '5px')};
`

// ---------------------------------------------------------------------------
// Pie charts (current vs recommended/your changes)
// ---------------------------------------------------------------------------

export const PieRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  column-gap: 64px;
  margin-top: 32px;

  @media (max-width: 600px) {
    column-gap: 24px;
  }
`

export const PieColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 10px;
  flex: 0 0 auto;
`

export const PieValue = styled.div`
  margin-top: 10px;
  font-family: ${FONT_FAMILY};
  font-size: 20px;
  font-weight: ${p => (p.planned ? 700 : 400)};
  color: ${p => (p.planned ? p.theme.primaryColor : p.theme.lightestGray)};

  @media (max-width: 600px) {
    font-size: 18px;
  }
`
